import {observe} from '@github/selector-observer'
import {on} from 'delegated-events'

const skewDataX = 'data-skew-x'
const skewDataY = 'data-skew-y'
const skewDataTargetX = 'data-skew-target-x'
const skewDataTargetY = 'data-skew-target-y'
const skewSpeed = 0.1
const skewStrength = 4

function skewTowardsTarget(skewTargetElement: HTMLElement) {
  const skew = getSkewState(skewTargetElement)
  if (skew.x === 0 && skew.y === 0 && skew.targetX === 0 && skew.targetY === 0) {
    skewTargetElement.setAttribute('data-animation-is-running', 'false')
    return
  }

  let skewX = skew.x + (skew.targetX - skew.x) * skewSpeed
  let skewY = skew.y + (skew.targetY - skew.y) * skewSpeed
  skewX = Math.round((skewX + Number.EPSILON) * 1000) / 1000
  skewY = Math.round((skewY + Number.EPSILON) * 1000) / 1000
  if (skewX * skewX < 0.005) skewX = 0
  if (skewY * skewY < 0.005) skewY = 0

  skewTargetElement.setAttribute(skewDataX, `${skewX}`)
  skewTargetElement.setAttribute(skewDataY, `${skewY}`)
  skewTargetElement.style.transform = `perspective(1000px) rotateX(${-skewX}deg) rotateY(${skewY}deg)`
  window.requestAnimationFrame(() => skewTowardsTarget(skewTargetElement))
}

function getSkewState(skewElement: HTMLElement) {
  const x = skewElement.getAttribute(skewDataX) || '0'
  const y = skewElement.getAttribute(skewDataY) || '0'
  const targetX = skewElement.getAttribute(skewDataTargetX) || '0'
  const targetY = skewElement.getAttribute(skewDataTargetY) || '0'

  return {
    x: parseFloat(x),
    y: parseFloat(y),
    targetX: parseFloat(targetX),
    targetY: parseFloat(targetY),
  }
}

observe('.js-readme-rotate', observed => {
  const image = observed as HTMLElement
  image.addEventListener('mousemove', (event: Event) => {
    const e = event as MouseEvent
    const skewTargetElement = e.target as HTMLElement
    if (skewTargetElement === null) return
    const rect = skewTargetElement.getBoundingClientRect()
    const cursorX = e.x - rect.left
    const cursorY = e.y - rect.top
    const relativeX = cursorX / rect.width
    const relativeY = cursorY / rect.height
    let skewTargetX = (relativeY - 0.5) * skewStrength
    let skewTargetY = (relativeX - 0.5) * skewStrength
    skewTargetX = Math.round((skewTargetX + Number.EPSILON) * 1000) / 1000
    skewTargetY = Math.round((skewTargetY + Number.EPSILON) * 1000) / 1000
    skewTargetElement.setAttribute(skewDataTargetX, `${skewTargetX}`)
    skewTargetElement.setAttribute(skewDataTargetY, `${skewTargetY}`)
  })

  image.addEventListener('mouseleave', (event: Event) => {
    const e = event as MouseEvent
    const skewTargetElement = e.target as HTMLElement
    if (skewTargetElement === null) return
    skewTargetElement.setAttribute(skewDataTargetX, '0')
    skewTargetElement.setAttribute(skewDataTargetY, '0')
  })

  image.addEventListener('mouseenter', () => {
    if (image.getAttribute('data-animation-is-running') === 'true') return

    image.setAttribute('data-animation-is-running', 'true')
    window.requestAnimationFrame(() => skewTowardsTarget(image))
  })
})

observe('.readme-story-body aside p', quote => {
  quote = quote as HTMLElement
  if (quote.textContent == null) return

  const link = document.createElement('a')
  link.classList.add('ml-2')
  link.classList.add('p-1')

  const quoteText = `“${quote.textContent}”`
  link.href = `https://twitter.com/intent/tweet?url=${encodeURI(window.location.href)}&text=${encodeURI(quoteText)}`

  link.setAttribute('aria-label', `Tweet: ${quote.textContent}`)
  link.setAttribute('target', `_blank`)
  link.setAttribute('rel', `noopener`)
  quote.appendChild(link)

  const label = link.appendChild(document.createElement('span'))
  label.classList.add('readme-story-blockquote-tweet')
})

on('click', '.js-readme-story-body-expand', function (event: Event) {
  event.preventDefault()
  const target = event.currentTarget as HTMLElement

  const container = target.closest<HTMLElement>('.readme-story-body-collapsed')!

  if (!container) {
    return
  }

  container.classList.remove('readme-story-body-collapsed')
})

on('submit', '.js-newsletter-signup-form', () => {
  const thankYou = document.querySelector<HTMLElement>('.js-newsletter-flash')
  thankYou!.classList.remove('v-hidden')
  thankYou!.ariaHidden = 'false'
  document.querySelector<HTMLInputElement>('.js-newsletter-email-input')!.value = ''
})

on('submit', '.js-nominate-form', event => {
  let isInvalid = false

  const inputFields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    '.js-nominate-form input[type="text"], .js-nominate-form textarea',
  )

  // Check if input fields are invalid. If invalid, toggle error message.
  for (const input of inputFields) {
    const element = input.localName
    const name = input.name
    const isBlank = input.value.trim() === ''

    // We expect the error message to be a sibling element for the current input
    const maybeErrorMsg = document.querySelector<HTMLParagraphElement>(`${element}[name="${name}"] ~ .js-error`)
    // eslint-disable-next-line github/no-d-none
    maybeErrorMsg?.classList.toggle('d-none', !isBlank)
    input.setAttribute('aria-invalid', String(isBlank))

    if (isBlank) {
      isInvalid = true
    }
  }

  if (isInvalid) {
    event.preventDefault()
    // For accessibility reasons, it's important to focus on the first invalid field.
    const maybeFirstInvalidInput: HTMLInputElement | HTMLTextAreaElement | null = document.querySelector(
      '.js-nominate-form input[aria-invalid="true"]',
    )
    maybeFirstInvalidInput?.focus()

    return
  }
})
